import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { CreateNewsDto, News, UpdateNewsDto } from '../../../types/news.types';
import { SystemImageUploadService } from '../../../service/supabase/SystemImageUploadService';

interface NewsDialogProps {
    visible: boolean;
    news: News | null;
    onHide: () => void;
    onSave: (data: CreateNewsDto | UpdateNewsDto) => void;
    isLoading: boolean;
    title: string;
}

export const NewsDialog: React.FC<NewsDialogProps> = ({
    visible,
    news,
    onHide,
    onSave,
    isLoading,
    title
}) => {
    const [formData, setFormData] = useState<CreateNewsDto | UpdateNewsDto>({
        title: '',
        content: '',
        url_image: '',
        url_banner: '',
        index: undefined
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const fileUploadImageRef = useRef<any>(null);
    const fileUploadBannerRef = useRef<any>(null);

    // Reset form when dialog visibility changes
    useEffect(() => {
        if (visible) {
            if (news) {
                setFormData({
                    title: news.title || '',
                    content: news.content || '',
                    url_image: news.url_image || '',
                    url_banner: news.url_banner || '',
                    index: news.index
                });
            } else {
                setFormData({
                    title: '',
                    content: '',
                    url_image: '',
                    url_banner: '',
                    index: undefined
                });
            }
            setFormErrors({});
        }
    }, [visible, news]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleNumberChange = (e: any) => {
        const value = e.value !== null && e.value !== undefined ? e.value : undefined;
        setFormData(prev => ({ ...prev, index: value }));

        // Clear error for index field
        if (formErrors.index) {
            setFormErrors(prev => ({ ...prev, index: '' }));
        }
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.title) {
            errors.title = 'Vui lòng nhập tiêu đề';
        }

        if (!formData.url_image) {
            errors.url_image = 'Vui lòng nhập URL hình ảnh';
        } else if (!isValidURL(formData.url_image)) {
            errors.url_image = 'URL hình ảnh không hợp lệ';
        }

        if (!formData.url_banner) {
            errors.url_banner = 'Vui lòng nhập URL liên kết';
        } else if (!isValidURL(formData.url_banner)) {
            errors.url_banner = 'URL liên kết không hợp lệ';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isValidURL = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch (e) {
            return false;
        }
    };

    const handleSave = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    const handleImageSelect = async (e: FileUploadSelectEvent) => {
        const file = e.files[0];
        if (!file) return;

        setUploadingImage(true);
        const { url, error } = await SystemImageUploadService.uploadNewsImage(file);
        setUploadingImage(false);

        if (error) {
            setFormErrors(prev => ({ ...prev, url_image: error }));
        } else if (url) {
            setFormData(prev => ({ ...prev, url_image: url }));
            if (formErrors.url_image) {
                setFormErrors(prev => ({ ...prev, url_image: '' }));
            }
        }

        // Clear the file upload
        if (fileUploadImageRef.current) {
            fileUploadImageRef.current.clear();
        }
    };

    const handleBannerSelect = async (e: FileUploadSelectEvent) => {
        const file = e.files[0];
        if (!file) return;

        setUploadingBanner(true);
        const { url, error } = await SystemImageUploadService.uploadNewsImage(file);
        setUploadingBanner(false);

        if (error) {
            setFormErrors(prev => ({ ...prev, url_banner: error }));
        } else if (url) {
            setFormData(prev => ({ ...prev, url_banner: url }));
            if (formErrors.url_banner) {
                setFormErrors(prev => ({ ...prev, url_banner: '' }));
            }
        }

        // Clear the file upload
        if (fileUploadBannerRef.current) {
            fileUploadBannerRef.current.clear();
        }
    };

    const renderFooter = () => {
        return (
            <div>
                <Button
                    label="Huỷ"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={onHide}
                    disabled={isLoading}
                />
                <Button
                    label="Lưu"
                    icon="pi pi-check"
                    className="p-button-primary"
                    onClick={handleSave}
                    loading={isLoading}
                />
            </div>
        );
    };

    return (
        <Dialog
            header={title}
            visible={visible}
            onHide={onHide}
            style={{ width: '450px' }}
            footer={renderFooter()}
            modal
            closable={!isLoading}
        >
            <div className="p-fluid">
                <div className="field">
                    <label htmlFor="title" className="font-bold">
                        Tiêu đề <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className={formErrors.title ? 'p-invalid' : ''}
                        disabled={isLoading}
                    />
                    {formErrors.title && <small className="p-error">{formErrors.title}</small>}
                </div>

                <div className="field">
                    <label htmlFor="content" className="font-bold">
                        Nội dung
                    </label>
                    <InputTextarea
                        id="content"
                        name="content"
                        value={formData.content || ''}
                        onChange={handleInputChange}
                        rows={5}
                        disabled={isLoading}
                    />
                </div>

                <div className="field">
                    <label htmlFor="url_image" className="font-bold">
                        URL Hình ảnh <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 align-items-center mb-2">
                        <FileUpload
                            ref={fileUploadImageRef}
                            mode="basic"
                            name="image"
                            accept="image/*"
                            maxFileSize={5000000}
                            onSelect={handleImageSelect}
                            auto={false}
                            chooseLabel="Chọn ảnh"
                            disabled={isLoading || uploadingImage}
                            className="flex-1"
                        />
                        {uploadingImage && <i className="pi pi-spin pi-spinner" />}
                    </div>
                    <InputText
                        id="url_image"
                        name="url_image"
                        value={formData.url_image}
                        onChange={handleInputChange}
                        className={formErrors.url_image ? 'p-invalid' : ''}
                        disabled={isLoading}
                        placeholder="Hoặc nhập URL trực tiếp"
                    />
                    {formErrors.url_image && <small className="p-error">{formErrors.url_image}</small>}
                    {formData.url_image && (
                        <div className="mt-2">
                            <Image src={formData.url_image} alt="Preview" width="200" preview />
                        </div>
                    )}
                </div>

                <div className="field">
                    <label htmlFor="url_banner" className="font-bold">
                        URL Liên kết (Bài viết) <span className="text-red-500">*</span>
                    </label>

                    <InputText
                        id="url_banner"
                        name="url_banner"
                        value={formData.url_banner}
                        onChange={handleInputChange}
                        className={formErrors.url_banner ? 'p-invalid' : ''}
                        disabled={isLoading}
                        placeholder="Hoặc nhập URL trực tiếp"
                    />
                    {formErrors.url_banner && <small className="p-error">{formErrors.url_banner}</small>}
                    {formData.url_banner && (
                        <div className="mt-2">
                            <Image src={formData.url_banner} alt="Preview" width="200" preview />
                        </div>
                    )}
                </div>

                <div className="field">
                    <label htmlFor="index" className="font-bold">
                        Thứ tự hiển thị
                    </label>
                    <InputNumber
                        id="index"
                        value={formData.index}
                        onValueChange={handleNumberChange}
                        min={0}
                        disabled={isLoading}
                    />
                </div>
            </div>
        </Dialog>
    );
};
